import { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import Button from 'components/Common/Button';
import MuiLoader from 'components/Common/Loader';
import { notify } from 'components/Common/Toast';
import Routes from 'constants/routes';
import { acceptMemberInvitation, previewMemberInvitation } from 'services/memberInvitationsApi';
import { getProblemDetail } from 'api/v1Helpers';
import { appClient } from 'api/apiClient';
import { AppDispatch, RootState } from 'redux/store';
import { fetchMyParishes, logoutUser, refreshMeStaffRoles } from 'redux/slices/user/userSlice';
import { selectCanAccessPortal } from 'redux/selectors/parishContext';
import {
    clearMemberInviteToken,
    isAuthRequiredMemberInviteError,
    isTerminalMemberInviteError,
    isWrongAccountMemberInviteError,
    readMemberInviteEmail,
    readMemberInviteToken,
    storeMemberInviteEmail,
    storeMemberInviteToken,
} from 'utils/memberInviteToken';
import './Auth.css';

type Phase = 'loading' | 'need-auth' | 'accepting' | 'success' | 'error';

const AcceptMemberInvite = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const accessToken = useSelector((s: RootState) => s.auth.data?.accessToken);
    const refreshToken = useSelector((s: RootState) => s.auth.data?.refreshToken);
    const userEmail = useSelector((s: RootState) => s.user.userInfo?.username || s.user.userResponse?.email);
    const canAccessPortal = useSelector(selectCanAccessPortal);
    const [phase, setPhase] = useState<Phase>('loading');
    const [message, setMessage] = useState('');
    const [inviteEmail, setInviteEmail] = useState(() => readMemberInviteEmail() || '');
    const [canRetryWithOtherAccount, setCanRetryWithOtherAccount] = useState(false);
    const [canRetryAccept, setCanRetryAccept] = useState(false);
    const attempted = useRef(false);
    const previewAttempted = useRef(false);
    const [acceptNonce, setAcceptNonce] = useState(0);

    const tokenFromQuery = searchParams.get('token') || '';
    const token = tokenFromQuery || readMemberInviteToken() || '';

    // Persist token then strip it from the visible URL (replace so history is clean).
    useEffect(() => {
        if (!tokenFromQuery) return;
        storeMemberInviteToken(tokenFromQuery);
        navigate(Routes.auth.acceptMemberInvite, { replace: true });
    }, [tokenFromQuery, navigate]);

    // Outside ProtectedRoute — rehydrated Redux tokens must be pushed onto appClient
    // or accept runs without Authorization and fails with "Missing bearer token".
    useEffect(() => {
        if (!accessToken) return;
        appClient.setToken(accessToken, refreshToken);
    }, [accessToken, refreshToken]);

    // Load invited email for registration lock (only needed while logged out).
    // Skip when authenticated so a preview failure cannot overwrite accept success.
    useEffect(() => {
        if (tokenFromQuery) return; // wait until URL is cleaned / token stored
        if (accessToken) return;
        if (!token || previewAttempted.current) return;
        if (readMemberInviteEmail()) {
            setInviteEmail(readMemberInviteEmail() || '');
            return;
        }
        previewAttempted.current = true;
        (async () => {
            try {
                const preview = await previewMemberInvitation(token);
                storeMemberInviteEmail(preview.email);
                setInviteEmail(preview.email);
            } catch (err) {
                const detail = getProblemDetail(err) || 'This invitation link is invalid or has expired.';
                if (isTerminalMemberInviteError(err)) {
                    clearMemberInviteToken();
                } else {
                    // Transient preview failure — allow another attempt.
                    previewAttempted.current = false;
                }
                setCanRetryWithOtherAccount(false);
                setCanRetryAccept(false);
                setPhase('error');
                setMessage(detail);
            }
        })();
    }, [token, tokenFromQuery, accessToken]);

    useEffect(() => {
        if (tokenFromQuery) return; // wait for replace navigation
        if (!token) {
            // If preview/accept already set an error and cleared the token, do not
            // overwrite that message with "missing a token".
            setPhase((p) => (p === 'error' || p === 'success' ? p : 'error'));
            setMessage((m) => m || 'This invitation link is missing a token.');
            setCanRetryWithOtherAccount(false);
            setCanRetryAccept(false);
            return;
        }
        if (!accessToken) {
            setPhase('need-auth');
            return;
        }
        if (attempted.current) return;
        attempted.current = true;
        setPhase('accepting');
        setCanRetryAccept(false);
        (async () => {
            try {
                // Tokens are already on appClient — the effect above pushes them and runs first.
                await acceptMemberInvitation(token);
                clearMemberInviteToken();
                // Hydrate staffRoles (/me) and memberships before success CTA.
                await Promise.all([dispatch(refreshMeStaffRoles()), dispatch(fetchMyParishes())]);
                setPhase('success');
                setMessage('You have successfully joined the parish.');
                notify('Invitation accepted.', 'success');
            } catch (err) {
                if (isAuthRequiredMemberInviteError(err)) {
                    attempted.current = false;
                    setCanRetryAccept(false);
                    setCanRetryWithOtherAccount(false);
                    setPhase('need-auth');
                    return;
                }
                const detail = getProblemDetail(err) || 'Could not accept this invitation.';
                const wrongAccount = isWrongAccountMemberInviteError(err);
                const terminal = isTerminalMemberInviteError(err);
                if (terminal) {
                    clearMemberInviteToken();
                }
                setCanRetryWithOtherAccount(wrongAccount);
                setCanRetryAccept(!terminal && !wrongAccount);
                setPhase('error');
                setMessage(detail);
            }
        })();
    }, [accessToken, token, tokenFromQuery, dispatch, acceptNonce]);

    const goLogin = () => navigate(Routes.auth.login);
    const goRegister = () => {
        const email = inviteEmail || readMemberInviteEmail() || '';
        navigate(Routes.auth.register, { state: { email, lockEmail: Boolean(email) } });
    };
    const switchAccount = async () => {
        await dispatch(logoutUser({ silent: true }));
        setPhase('need-auth');
        attempted.current = false;
        setCanRetryAccept(false);
    };
    const retryAccept = () => {
        attempted.current = false;
        setCanRetryAccept(false);
        setAcceptNonce((n) => n + 1);
    };
    const signOut = async () => {
        await dispatch(logoutUser({ silent: true }));
        navigate(Routes.auth.login, { replace: true });
    };

    return (
        <AuthLayout>
            <div className="Auth-form">
                <Box p={4}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Parish invitation
                    </Typography>
                    {(phase === 'loading' || phase === 'accepting') && <MuiLoader loading absolute />}
                    {phase === 'need-auth' && (
                        <Stack gap={2}>
                            <Typography>
                                Sign in with the email that received this invitation, or create an account with that email, then return here
                                to accept.
                            </Typography>
                            {inviteEmail ? (
                                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Invitation for: {inviteEmail}</Typography>
                            ) : null}
                            {userEmail ? (
                                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Current session: {String(userEmail)}</Typography>
                            ) : null}
                            <Button title="Sign in" onClick={goLogin} />
                            <Button title="Create account" onClick={goRegister} variant="outlined" disabled={!inviteEmail} />
                        </Stack>
                    )}
                    {phase === 'success' && (
                        <Stack gap={2}>
                            <Typography>{message}</Typography>
                            {canAccessPortal ? (
                                <Button title="Go to portal" onClick={() => navigate(Routes.home.main)} />
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                                        This account does not have staff access to the portal. You can sign out when you are done.
                                    </Typography>
                                    <Button title="Sign out" onClick={signOut} />
                                </>
                            )}
                        </Stack>
                    )}
                    {phase === 'error' && (
                        <Stack gap={2}>
                            <Typography color="error">{message}</Typography>
                            {canRetryAccept ? <Button title="Try again" onClick={retryAccept} /> : null}
                            {canRetryWithOtherAccount ? <Button title="Try again with another account" onClick={switchAccount} /> : null}
                            <Button title="Sign in" onClick={goLogin} variant="outlined" />
                        </Stack>
                    )}
                </Box>
            </div>
        </AuthLayout>
    );
};

export default AcceptMemberInvite;
